import * as React from "react";

import { DataExtractor, getExtractor } from "../../utils/dataView";
import {
    SimpleAndFilter,
    SimpleFieldFilter,
    QueryResult,
    AsyncDataViewIndex,
} from "../../utils/indexing";
import { DeferredContent } from "../deferredContent";
import { FilterTags } from "../filterTags";
import { AsyncFilterTypeahead } from "../filterTypeahead/asyncFilterTypeahead";
import type {
    AsyncFilterTypeaheadProps,
    ExtractorRef,
} from "../filterTypeahead/asyncFilterTypeahead";

import * as theme from "./theme.css";

class AsyncFilter<ITEM> extends React.Component<AsyncFilterProps<ITEM>> {
    render() {
        const {
            id,
            queryResult,
            hideTypeahead,
            hideResultCount,
            filter,
            children,
            dataViewIndex,
            extractorRefs,
            onAddFilter,
            onRemoveFilter,
            onResetFilter,
            placeholder,
            disabled,
        } = this.props;
        const AsyncFilterTypeaheadElt: new (
            props: AsyncFilterTypeaheadProps<ITEM>
        ) => React.Component<AsyncFilterTypeaheadProps<ITEM>, any> =
            AsyncFilterTypeahead;
        const toDisplayFilter = (
            extractorId: string,
            filter: SimpleFieldFilter
        ) => dataViewIndex.toDisplayFilter(extractorId, filter);
        return (
            <div>
                <div className={theme.topContainer}>
                    {hideTypeahead ? null : (
                        <div className={theme.typeaheadContainer}>
                            <AsyncFilterTypeaheadElt
                                id={id}
                                queryResult={queryResult}
                                filter={filter}
                                hideResultCount={hideResultCount}
                                dataViewIndex={dataViewIndex}
                                extractorRefs={extractorRefs}
                                placeholder={placeholder}
                                onFilterSelected={onAddFilter}
                                disabled={disabled}
                            />
                        </div>
                    )}
                    {children ? (
                        <div className={theme.children}>{children}</div>
                    ) : null}
                </div>
                {filter !== null ? (
                    <DeferredContent
                        promise={queryResult}
                        renderContent={(queryResult) => {
                            return (
                                <div className={theme.filterContainer}>
                                    <FilterTags
                                        filter={filter}
                                        onRemoveFilter={onRemoveFilter}
                                        onResetFilter={onResetFilter}
                                        getExtractor={(extractorId: string) =>
                                            getExtractor(
                                                queryResult.dataView,
                                                extractorId
                                            ) as DataExtractor<unknown, unknown>
                                        }
                                        toDisplayFilter={toDisplayFilter}
                                    />
                                </div>
                            );
                        }}
                    />
                ) : null}
            </div>
        );
    }
}

interface AsyncFilterProps<ITEM> {
    id: string;
    queryResult: Promise<QueryResult<ITEM>>;
    dataViewIndex: AsyncDataViewIndex<ITEM>;
    hideTypeahead?: boolean;
    hideResultCount?: boolean;
    filter: SimpleAndFilter | null;
    children?: React.ReactNode;
    extractorRefs: Array<ExtractorRef>;
    onAddFilter: (
        extractorId: string,
        filter: SimpleFieldFilter,
        excluded: boolean
    ) => void;
    onRemoveFilter: (
        extractorId: string,
        filter: SimpleFieldFilter,
        excluded: boolean
    ) => void;
    onResetFilter: () => void;
    placeholder?: React.ReactNode;
    disabled?: boolean;
}

class AsyncTableFilter<ITEM> extends React.PureComponent<
    AsyncTableFilterProps<ITEM>
> {
    render = () => {
        const { hideTypeahead, filter, children } = this.props;
        const hideEverything = hideTypeahead && !children && filter === null;
        return hideEverything ? null : (
            <div className={theme.tableFilter}>
                <AsyncFilter<ITEM> {...this.props} />
            </div>
        );
    };
}

type AsyncTableFilterProps<ITEM> = AsyncFilterProps<ITEM>;

export {
    ExtractorRef,
    AsyncFilter,
    type AsyncFilterProps,
    AsyncTableFilter,
    type AsyncTableFilterProps,
};
